import {
    calcPMT,
    calcApproxEffectiveRate,
    calcMlade,
    calcMaxLoan,
    calcInterkalarna
} from './calcEngine.js';

const uiCtx = {
    config: {},
    state: {},
    formsByType: new Map(),
    panelsByType: new Map(),
    lastResultByType: new Map(),
    amortizationRowsByType: new Map(),
    activeType: 'variable',
    isPdfBusy: false
};

const CANONICAL_YOUNG_TYPE = 'subvention-young';
const CANONICAL_CREDIT_CAPACITY_SLUG = 'credit-capacity';

const calcTypeToUrlSlug = {
    sposobnost: CANONICAL_CREDIT_CAPACITY_SLUG
};

const urlSlugToCalcType = {
    [CANONICAL_CREDIT_CAPACITY_SLUG]: 'sposobnost'
};

const CALC_TYPE_SLUG_SR_CSV = {
    'subvention-young': 'kredit-za-mlade',
    fixed: 'fiksni-kredit',
    variable: 'varijabilni-kredit',
    mortgage: 'stambeni-kredit'
};

const PDF_EXPORT_TIMEOUT_MS = 30000;

let pendingFrame = false;
const writeQueue = [];
const dismissedBanners = new Set();

function batchDomWrite(cb) {
    writeQueue.push(cb);
    if (pendingFrame) return;
    pendingFrame = true;

    requestAnimationFrame(() => {
        pendingFrame = false;
        const queue = writeQueue.splice(0, writeQueue.length);
        queue.forEach((fn) => {
            try {
                fn();
            } catch (error) {
                // Continue draining even if one DOM write callback throws.
            }
        });
    });
}

function debounce(fn, wait = 400) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
    };
}

function toNumber(value, fallback = 0) {
    if (value === null || value === undefined) {
        return fallback;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return fallback;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value) {
    const safe = Number.isFinite(Number(value)) ? Number(value) : 0;
    return `${safe.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
}

function formatPercent(value) {
    const safe = Number.isFinite(Number(value)) ? Number(value) : 0;
    return `${safe.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

const MLADE_MODEL_FIELDS = ['grossRatePct', 'clientRatePct', 'subsidyMonths', 'currency'];

function normalizeCalcType(type) {
    const raw = String(type || '').trim();
    if (!raw) return raw;
    return urlSlugToCalcType[raw] || raw;
}

function toCalculatorUrlSlug(type) {
    const normalized = normalizeCalcType(type);
    return calcTypeToUrlSlug[normalized] || normalized;
}

function buildCalculatorUrl(type, query = '') {
    const slug = toCalculatorUrlSlug(type);
    return query ? `/calculators/${slug}?${query}` : `/calculators/${slug}`;
}

function isYoungType(type) {
    return normalizeCalcType(type) === CANONICAL_YOUNG_TYPE;
}

function parseNumericInput(form, name) {
    const input = getFormInput(form, name);
    if (!input) return null;
    if (input.value === null || input.value === undefined || String(input.value).trim() === '') {
        return null;
    }

    const value = Number(input.value);
    return Number.isFinite(value) ? value : null;
}

function parseStringInput(form, name) {
    const input = getFormInput(form, name);
    if (!input) return null;
    const value = String(input.value || '').trim();
    return value || null;
}

function ensureMladeModelInputs(form) {
    if (!form || !isYoungType(form.dataset.calcType)) return;

    MLADE_MODEL_FIELDS.forEach((fieldName) => {
        if (getFormInput(form, fieldName)) return;

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = fieldName;
        hidden.dataset.modelField = CANONICAL_YOUNG_TYPE;
        form.appendChild(hidden);
    });
}

function getMladePayloadFromForm(form) {
    ensureMladeModelInputs(form);

    const loanAmount = parseNumericInput(form, 'loanAmount');
    const totalYears = parseNumericInput(form, 'totalYears');
    const applicantAge = parseNumericInput(form, 'applicantAge');

    const bankMarginPct = parseNumericInput(form, 'bankMarginPct') ?? 2.0;
    const euriborRate = parseNumericInput(form, 'euriborRate');
    const euriborTenor = parseStringInput(form, 'euriborTenor') || '6M';
    const grossRatePct = 3.5;
    const clientRatePct = 1.5;
    const subsidyMonths = 72;

    return {
        loanAmount,
        totalYears,
        grossRatePct,
        clientRatePct,
        bankMarginPct,
        euriborTenor,
        euriborRate: Number.isFinite(euriborRate) ? euriborRate : null,
        subsidyMonths,
        currency: 'EUR',
        applicantAge
    };
}

function isValidYoungTotalYears(totalYears) {
    return Number.isInteger(totalYears) && totalYears >= 7 && totalYears <= 40;
}

function isValidYoungLoanAmount(loanAmount) {
    return Number.isFinite(loanAmount) && loanAmount > 0;
}

function isValidYoungBankMargin(bankMarginPct) {
    return Number.isFinite(bankMarginPct) && bankMarginPct >= 0 && bankMarginPct <= 10;
}

function isValidYoungEuriborTenor(euriborTenor) {
    return euriborTenor === '3M' || euriborTenor === '6M';
}

function isValidYoungEuriborRate(euriborRate) {
    if (euriborRate === null) return true;
    return Number.isFinite(euriborRate) && euriborRate >= -5 && euriborRate <= 30;
}

function getSubventionYoungPayloadValidationMessage(payload) {
    if (!isValidYoungTotalYears(payload.totalYears)) {
        return 'Ukupan rok otplate mora biti ceo broj u opsegu od 7 do 40 godina.';
    }

    if (!isValidYoungLoanAmount(payload.loanAmount)) {
        return 'Iznos kredita mora biti veći od 0.';
    }

    if (!isValidYoungBankMargin(payload.bankMarginPct)) {
        return 'Marža banke mora biti u opsegu od 0 do 10%.';
    }

    if (!isValidYoungEuriborTenor(payload.euriborTenor)) {
        return 'EURIBOR period mora biti 3M ili 6M.';
    }

    if (!isValidYoungEuriborRate(payload.euriborRate)) {
        return 'EURIBOR stopa mora biti prazna ili u opsegu od -5 do 30.';
    }

    return '';
}

function buildSubventionYoungRequestPayloadFromForm(form, includeAmortization = false) {
    const basePayload = getMladePayloadFromForm(form);
    const payload = {
        loanAmount: Number(basePayload.loanAmount),
        totalYears: Number(basePayload.totalYears),
        bankMarginPct: Number(basePayload.bankMarginPct),
        euriborRate: basePayload.euriborRate === null ? null : Number(basePayload.euriborRate),
        euriborTenor: basePayload.euriborTenor,
        grossRatePct: Number(basePayload.grossRatePct),
        clientRatePct: Number(basePayload.clientRatePct),
        subsidyMonths: Number(basePayload.subsidyMonths),
        currency: String(basePayload.currency || 'EUR')
    };

    if (includeAmortization) {
        payload.includeAmortization = true;
    }

    return payload;
}

function syncMladeModelInputs(form, payload) {
    if (!form || !payload) return;
    ensureMladeModelInputs(form);

    const setValue = (name, value) => {
        const input = getFormInput(form, name);
        if (!input) return;
        input.value = value === null || value === undefined ? '' : String(value);
    };

    setValue('grossRatePct', payload.grossRatePct);
    setValue('clientRatePct', payload.clientRatePct);
    setValue('subsidyMonths', payload.subsidyMonths || 72);
    setValue('currency', payload.currency || 'EUR');
}

function updateMladeRateLabels(form, result) {
    const card = form ? form.querySelector('.calc-result') : null;
    if (!card) return;

    const phaseOneRate = Number(result?.clientPhase1RatePct ?? result?.clientRatePct ?? 1.5);
    const phaseTwoRate = Number(result?.postSubsidyRatePct);

    const subsidyField = card.querySelector('[data-field="subsidyPayment"]');
    const marketField = card.querySelector('[data-field="marketPayment"]');
    const subsidyLabel = subsidyField ? subsidyField.closest('.calc-result-item')?.querySelector('.calc-result-label') : null;
    const marketLabel = marketField ? marketField.closest('.calc-result-item')?.querySelector('.calc-result-label') : null;

    if (subsidyLabel) {
        subsidyLabel.textContent = Number.isFinite(phaseOneRate)
            ? `Rata (prvih 72 meseca, korisnik plaća ${formatPercent(phaseOneRate)})`
            : 'Rata (subvencionisani period)';
    }

    if (marketLabel) {
        marketLabel.textContent = Number.isFinite(phaseTwoRate)
            ? `Rata (nakon subvencije, marža + EURIBOR = ${formatPercent(phaseTwoRate)})`
            : 'Rata (nakon subvencije)';
    }
}

function buildLoanScheduleHref(calcType, form) {
    if (!form) return '/loan-schedule';

    const params = new URLSearchParams();
    params.set('paymentsPerYear', '12');

    if (calcType === 'fixed') {
        const loanAmount = toNumber(getFormInput(form, 'loanAmount')?.value);
        const annualRate = toNumber(getFormInput(form, 'annualRate')?.value);
        const termYears = toNumber(getFormInput(form, 'termYears')?.value);
        if (loanAmount > 0 && termYears > 0) {
            params.set('loanAmount', String(loanAmount));
            params.set('annualRate', String(annualRate));
            params.set('termYears', String(termYears));
        } else {
            return '/loan-schedule';
        }
    } else if (calcType === 'variable') {
        const introRateInput = getFormInput(form, 'introRate');
        const introPeriodYearsInput = getFormInput(form, 'introPeriodYears');
        const euriborRateInput = getFormInput(form, 'euriborRate');
        const bankMarginInput = getFormInput(form, 'bankMargin');
        const loanAmount = toNumber(getFormInput(form, 'loanAmount')?.value);
        const termYears = toNumber(getFormInput(form, 'termYears')?.value);
        const introPeriodYears = toNumber(introPeriodYearsInput?.value);
        const introRate = toNumber(introRateInput?.value);
        const euriborRate = toNumber(euriborRateInput?.value);
        const bankMargin = toNumber(bankMarginInput?.value);
        const annualRate = introPeriodYears > 0
            ? introRate
            : Math.round((euriborRate + bankMargin) * 1e10) / 1e10;

        const hasIntroRate = introRateInput && introRateInput.value !== '' && Number.isFinite(Number(introRateInput.value));
        const hasIntroPeriodYears = introPeriodYearsInput && introPeriodYearsInput.value !== '' && Number.isFinite(Number(introPeriodYearsInput.value));
        const hasEuriborRate = euriborRateInput && euriborRateInput.value !== '' && Number.isFinite(Number(euriborRateInput.value));
        const hasBankMargin = bankMarginInput && bankMarginInput.value !== '' && Number.isFinite(Number(bankMarginInput.value));

        if (loanAmount > 0 && termYears > 0 && annualRate > 0) {
            params.set('loanAmount', String(loanAmount));
            params.set('annualRate', String(annualRate));
            params.set('termYears', String(termYears));

            if (introPeriodYears > 0 && hasIntroRate) {
                params.set('introRate', String(introRate));
            }
            if (introPeriodYears > 0 && hasIntroPeriodYears) {
                params.set('introPeriodYears', String(introPeriodYears));
            }
            if (introPeriodYears > 0 && hasEuriborRate) {
                params.set('euriborRate', String(euriborRate));
            }
            if (introPeriodYears > 0 && hasBankMargin) {
                params.set('bankMargin', String(bankMargin));
            }
        } else {
            return '/loan-schedule';
        }
    } else if (isYoungType(calcType)) {
        const mladePayload = getMladePayloadFromForm(form);
        const loanAmount = toNumber(mladePayload.loanAmount);
        const annualRate = toNumber(mladePayload.clientRatePct, toNumber(mladePayload.grossRatePct));
        const termYears = toNumber(mladePayload.totalYears);
        const introPeriodYears = toNumber((mladePayload.subsidyMonths || 72) / 12);
        const introRate = annualRate;
        const euriborRate = Number.isFinite(Number(mladePayload.euriborRate))
            ? Number(mladePayload.euriborRate)
            : null;
        const bankMargin = Number.isFinite(Number(mladePayload.bankMarginPct))
            ? Number(mladePayload.bankMarginPct)
            : null;
        const hasEuriborPair = Number.isFinite(euriborRate) && Number.isFinite(bankMargin);
        const followRate = hasEuriborPair
            ? euriborRate + bankMargin
            : toNumber(mladePayload.grossRatePct);

        if (loanAmount > 0 && termYears > 0) {
            params.set('loanAmount', String(loanAmount));
            params.set('annualRate', String(annualRate));
            params.set('termYears', String(termYears));
            params.set('introRate', String(introRate));
            params.set('introPeriodYears', String(introPeriodYears));
            if (!hasEuriborPair) {
                params.set('followRate', String(followRate));
            }

            if (Number.isFinite(euriborRate)) {
                params.set('euriborRate', String(euriborRate));
            }

            if (Number.isFinite(bankMargin)) {
                params.set('bankMargin', String(bankMargin));
            }
        } else {
            return '/loan-schedule';
        }
    } else if (calcType === 'sposobnost') {
        const result = uiCtx.lastResultByType.get('sposobnost');
        const maxLoan = toNumber(result?.maxLoanAmount);
        const annualRate = toNumber(getFormInput(form, 'annualRate')?.value);
        const termYears = toNumber(getFormInput(form, 'termYears')?.value);
        if (maxLoan > 0 && termYears > 0) {
            params.set('loanAmount', String(Math.round(maxLoan)));
            params.set('annualRate', String(annualRate));
            params.set('termYears', String(termYears));
        } else {
            return '/loan-schedule';
        }
    } else {
        return '/loan-schedule';
    }

    return `/loan-schedule?${params.toString()}`;
}

function updateLoanScheduleLink(calcType) {
    const panel = getPanel(calcType);
    if (!panel) return;
    const link = panel.querySelector('[data-action="loan-schedule"]');
    if (!link) return;

    batchDomWrite(() => {
        link.hidden = false;
        link.removeAttribute('aria-hidden');
    });

    const form = getForm(calcType);
    link.href = buildLoanScheduleHref(calcType, form);
}

function parseJsonSafe(raw, fallback) {
    try {
        return JSON.parse(raw);
    } catch (error) {
        return fallback;
    }
}

function setControlDisabledState(control, disabled) {
    if (!control) return;

    if (control.tagName === 'BUTTON') {
        control.disabled = disabled;
        return;
    }

    if (control.tagName === 'A') {
        if (disabled) {
            if (!control.dataset.prevTabindex) {
                control.dataset.prevTabindex = control.getAttribute('tabindex') || '';
            }
            control.setAttribute('aria-disabled', 'true');
            control.setAttribute('tabindex', '-1');
            control.classList.add('is-disabled');
        } else {
            control.removeAttribute('aria-disabled');
            const previousTabIndex = control.dataset.prevTabindex;
            if (previousTabIndex) {
                control.setAttribute('tabindex', previousTabIndex);
            } else {
                control.removeAttribute('tabindex');
            }
            delete control.dataset.prevTabindex;
            control.classList.remove('is-disabled');
        }
    }
}

function ensurePdfOverlay(root) {
    if (!root) return null;

    const existing = root.querySelector('.calc-export-overlay');
    if (existing) return existing;

    const overlay = document.createElement('div');
    overlay.className = 'calc-export-overlay';
    overlay.hidden = true;
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-atomic', 'true');

    const spinner = document.createElement('span');
    spinner.className = 'calc-export-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.className = 'calc-export-text';
    text.textContent = 'PDF se generiše. Sačekajte...';

    overlay.appendChild(spinner);
    overlay.appendChild(text);
    root.appendChild(overlay);

    return overlay;
}

function setPdfBusyState(isBusy) {
    const root = document.querySelector('.calc-page');
    const overlay = ensurePdfOverlay(root);
    uiCtx.isPdfBusy = isBusy;

    if (root) {
        root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
        root.classList.toggle('is-export-busy', isBusy);
    }

    if (overlay) {
        overlay.hidden = !isBusy;
    }

    document.querySelectorAll('.calc-tab, .calc-actions .calc-btn').forEach((control) => {
        setControlDisabledState(control, isBusy);
    });
}

function getAuthToken() {
    const root = document.querySelector('.calc-page');
    if (!root) return null;

    const token = root.getAttribute('data-auth-token');
    if (typeof token !== 'string') return null;

    const trimmed = token.trim();
    return trimmed || null;
}

function getPanel(type) {
    return uiCtx.panelsByType.get(normalizeCalcType(type)) || null;
}

function getForm(type) {
    return uiCtx.formsByType.get(normalizeCalcType(type)) || null;
}

function setPanelMessage(panel, message, tone = 'info') {
    if (!panel) return;
    const el = panel.querySelector('.calc-inline-message');
    if (!el) return;
    batchDomWrite(() => {
        el.textContent = message || '';
        el.dataset.tone = tone;
        el.hidden = !message;
    });
}

function getFormInput(form, name) {
    return form ? form.querySelector(`[name="${name}"]`) : null;
}

function serializeFormToParams(form) {
    const params = new URLSearchParams();
    if (!form) return params;

    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
        if (value === '' || value === null || value === undefined) continue;
        params.set(key, String(value));
    }
    return params;
}

function updateUrlForType(type) {
    const normalizedType = normalizeCalcType(type);
    const form = getForm(normalizedType);
    const params = serializeFormToParams(form);
    const query = params.toString();
    const url = buildCalculatorUrl(normalizedType, query);
    window.history.replaceState({}, '', url);
}

function showBannerForTab(type) {
    const banner = document.querySelector(`[data-tab-banner="${type}"]`);
    if (!banner) return;

    if (dismissedBanners.has(type)) {
        batchDomWrite(() => {
            banner.hidden = true;
        });
        return;
    }

    batchDomWrite(() => {
        banner.hidden = false;
        banner.classList.remove('is-dismissing');
    });
}

function initBannerDismiss() {
    document.querySelectorAll('.calc-banner-close').forEach((btn) => {
        btn.addEventListener('click', () => {
            const banner = btn.closest('[data-tab-banner]');
            if (!banner) return;

            const type = banner.dataset.tabBanner;
            dismissedBanners.add(type);
            banner.classList.add('is-dismissing');
            setTimeout(() => {
                banner.hidden = true;
            }, 220);
        });
    });
}

function renderResult(form, result, fieldFormatters = {}) {
    if (!form) return;
    const card = form.querySelector('.calc-result');
    if (!card) return;

    const updates = [];
    const fields = card.querySelectorAll('[data-field]');
    fields.forEach((fieldEl) => {
        const key = fieldEl.dataset.field;
        const rawValue = result[key];
        const formatter = fieldFormatters[key] || formatCurrency;
        const formatted = rawValue === null || rawValue === undefined ? '—' : formatter(rawValue);
        updates.push(() => {
            const isInitialLoad = fieldEl.classList.contains('skeleton');
            if (!isInitialLoad) {
                fieldEl.classList.add('is-updating');
            }
            setTimeout(() => {
                fieldEl.textContent = formatted;
                fieldEl.classList.remove('skeleton');
                fieldEl.classList.remove('is-updating');
            }, isInitialLoad ? 0 : 80);
        });
    });

    updates.push(() => {
        card.classList.remove('is-loading');
        card.setAttribute('aria-busy', 'false');
    });

    batchDomWrite(() => updates.forEach((fn) => fn()));
}

function setFormResult(calcType, form, result) {
    uiCtx.lastResultByType.set(calcType, result);
    if (form) {
        form.dataset.lastResult = JSON.stringify(result);
    }

    const detail = {
        calcType,
        inputJson: collectFormInput(form),
        resultJson: result
    };
    document.dispatchEvent(new CustomEvent('loanCalc:result-updated', { detail }));

    updateLoanScheduleLink(calcType);
}

function collectFormInput(form) {
    const payload = {};
    if (!form) return payload;

    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
        if (value === '') continue;
        const numeric = Number(value);
        payload[key] = Number.isFinite(numeric) ? numeric : value;
    }

    return payload;
}

function getMladeAgeLimit(config) {
    const candidates = [
        config?.mladeMaxAge,
        config?.maxApplicantAge,
        config?.mlade?.maxAge,
        config?.MLADE_MAX_AGE,
        config?.kreditZaMladeMaxAge,
        config?.rules?.mlade?.maxAge
    ];

    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric) && numeric > 0) {
            return numeric;
        }
    }

    return 35;
}

function applyMladeEligibility(form) {
    if (!form) return true;

    const alert = form.querySelector('.calc-eligibility-alert');
    const resultCard = form.querySelector('.calc-result');
    const ageInput = getFormInput(form, 'applicantAge');
    const applicantAgeInput = getFormInput(form, 'applicantAge');
    const maxAge = getMladeAgeLimit(uiCtx.config);
    const age = toNumber(ageInput?.value, 0);

    const eligible = age > 0 && age <= maxAge;

    batchDomWrite(() => {
        if (!alert || !resultCard || !applicantAgeInput) return;

        if (!eligible) {
            alert.hidden = false;
            alert.textContent = `Kredit za mlade je dostupan za starost do ${maxAge} godina.`;
            resultCard.hidden = true;
            applicantAgeInput.classList.add('is-invalid');
            applicantAgeInput.setCustomValidity('Starost podnosioca je iznad dozvoljene granice za kredit za mlade.');
        } else {
            alert.hidden = true;
            alert.textContent = '';
            resultCard.hidden = false;
            applicantAgeInput.classList.remove('is-invalid');
            applicantAgeInput.setCustomValidity('');
        }
    });

    return eligible;
}

function runMortgageCalc(form, calcType) {
    const loanAmount = toNumber(getFormInput(form, 'loanAmount')?.value);
    const annualRate = toNumber(getFormInput(form, 'annualRate')?.value);
    const termYears = toNumber(getFormInput(form, 'termYears')?.value);
    const paymentsPerYear = 12;
    const totalPeriods = termYears * paymentsPerYear;
    const periodicRate = annualRate / 100 / paymentsPerYear;

    if (!loanAmount || !termYears) return;

    const payment = calcPMT(loanAmount, periodicRate, totalPeriods);
    const totalPayment = payment * totalPeriods;
    const totalInterest = totalPayment - loanAmount;
    const approxEffectiveRatePct = calcApproxEffectiveRate(annualRate, paymentsPerYear);
    const interkalarnaInterest = calcInterkalarna(loanAmount, annualRate, 30);

    const result = {
        loanAmount,
        annualRate,
        termYears,
        calcType,
        payment,
        totalPayment,
        totalInterest,
        approxEffectiveRatePct,
        interkalarnaInterest
    };

    renderResult(form, result, {
        approxEffectiveRatePct: formatPercent
    });

    setFormResult(calcType, form, result);
}

function getNestedFieldValue(source, key) {
    if (!source || !key) return undefined;
    const normalized = String(key).replace(/\[(\d+)\]/g, '.$1');
    const parts = normalized.split('.').filter(Boolean);
    let current = source;

    for (const part of parts) {
        if (current === null || current === undefined) {
            return undefined;
        }
        current = current[part];
    }

    return current;
}

function toggleResultRow(card, fieldName, shouldShow) {
    if (!card) return;
    const valueEl = card.querySelector(`[data-field="${fieldName}"]`);
    const row = valueEl ? valueEl.closest('.calc-result-item') : null;
    if (!row) return;

    row.hidden = !shouldShow;
    if (shouldShow) {
        row.removeAttribute('aria-hidden');
    } else {
        row.setAttribute('aria-hidden', 'true');
    }
}

function renderVariableResult(form, data) {
    if (!form) return;
    const card = form.querySelector('.calc-result');
    if (!card) return;

    const introYears = toNumber(data?.introYears, toNumber(getFormInput(form, 'introPeriodYears')?.value));
    const totalYears = toNumber(data?.totalYears, toNumber(getFormInput(form, 'termYears')?.value));
    const hasIntroPhase = introYears > 0;
    const hasVariablePhase = introYears < totalYears;

    const resultItems = card.querySelectorAll('[data-field]');
    const updates = [];

    resultItems.forEach((fieldEl) => {
        const key = fieldEl.dataset.field;
        const rawValue = getNestedFieldValue(data, key);
        const formatter = key === 'approxEffectiveRatePct' ? formatPercent : formatCurrency;
        const formatted = rawValue === null || rawValue === undefined ? '—' : formatter(rawValue);

        updates.push(() => {
            const isInitialLoad = fieldEl.classList.contains('skeleton');
            if (!isInitialLoad) {
                fieldEl.classList.add('is-updating');
            }
            setTimeout(() => {
                fieldEl.textContent = formatted;
                fieldEl.classList.remove('skeleton');
                fieldEl.classList.remove('is-updating');
            }, isInitialLoad ? 0 : 80);
        });
    });

    updates.push(() => {
        toggleResultRow(card, 'introPayment', hasIntroPhase);
        toggleResultRow(card, 'variablePayment', hasVariablePhase);
        toggleResultRow(card, 'scenarios[1].payment', hasVariablePhase);
        toggleResultRow(card, 'scenarios[2].payment', hasVariablePhase);
    });

    updates.push(() => {
        const introLabelSpan = card.querySelector('[data-year-label="intro"]');
        const variableLabelSpan = card.querySelector('[data-year-label="variable"]');
        if (introLabelSpan) {
            introLabelSpan.textContent = introYears > 0 ? `god. 1-${introYears}` : 'god. 1-?';
        }
        if (variableLabelSpan && hasVariablePhase) {
            variableLabelSpan.textContent = `god. ${introYears + 1}-${totalYears}`;
        }
    });

    updates.push(() => {
        card.classList.remove('is-loading');
        card.setAttribute('aria-busy', 'false');
    });

    batchDomWrite(() => updates.forEach((fn) => fn()));
}

async function runVariableCalc(form) {
    if (!form) return;

    const loanAmount = toNumber(getFormInput(form, 'loanAmount')?.value);
    const termYears = toNumber(getFormInput(form, 'termYears')?.value);
    const introPeriodYears = toNumber(getFormInput(form, 'introPeriodYears')?.value);
    const introRate = toNumber(getFormInput(form, 'introRate')?.value);
    const euriborRateInput = getFormInput(form, 'euriborRate');
    const rawEuriborRate = euriborRateInput?.value;
    const hasEuriborRate = rawEuriborRate !== null
        && rawEuriborRate !== undefined
        && String(rawEuriborRate).trim() !== '';
    const euriborRate = hasEuriborRate ? toNumber(rawEuriborRate, null) : null;
    const euriborTenor = getFormInput(form, 'euriborTenor')?.value || '3M';
    const bankMargin = toNumber(getFormInput(form, 'bankMargin')?.value);

    if (!loanAmount || !termYears) return;

    const payload = {
        loanAmount,
        totalYears: termYears,
        introYears: introPeriodYears,
        ...(introPeriodYears > 0 && { introRate }),
        euriborRate,
        euriborTenor,
        bankMargin,
        paymentsPerYear: 12,
        includeAmortization: false
    };

    try {
        const data = await fetchJson('/calculators/variable', payload);
        renderVariableResult(form, data);
        setFormResult('variable', form, data);
    } catch (error) {
        const panel = getPanel('variable');
        setPanelMessage(panel, 'Proverite vrednosti u poljima i pokušajte ponovo.', 'error');
    }
}

function runMladeCalc(form) {
    if (!applyMladeEligibility(form)) {
        return;
    }

    const payload = getMladePayloadFromForm(form);
    if (!payload.loanAmount || !payload.totalYears) return;

    syncMladeModelInputs(form, payload);

    let result;
    try {
        result = calcMlade(payload);
    } catch (error) {
        const panel = getPanel(CANONICAL_YOUNG_TYPE);
        setPanelMessage(panel, 'Proverite vrednosti u poljima i pokušajte ponovo.', 'error');
        return;
    }

    updateMladeRateLabels(form, result);

    renderResult(form, result, {
        approxEffectiveRatePct: formatPercent
    });

    if (result.principalExceedsSubsidyCap) {
        setPanelMessage(
            getPanel(CANONICAL_YOUNG_TYPE),
            'Iznos kredita prelazi prag subvencije od 100.000 EUR. Proverite uslove subvencije kod banke.',
            'warn'
        );
    } else {
        setPanelMessage(getPanel(CANONICAL_YOUNG_TYPE), '', 'info');
    }

    setFormResult(CANONICAL_YOUNG_TYPE, form, result);
}

function runSposobnostCalc(form) {
    const monthlyIncome = toNumber(getFormInput(form, 'monthlyIncome')?.value);
    const monthlyObligations = toNumber(getFormInput(form, 'monthlyObligations')?.value);
    const annualRate = toNumber(getFormInput(form, 'annualRate')?.value);
    const termYears = toNumber(getFormInput(form, 'termYears')?.value);
    const debtToIncomeRatio = toNumber(getFormInput(form, 'debtToIncomeRatio')?.value) / 100;

    const periodicRate = annualRate / 100 / 12;
    const totalPeriods = termYears * 12;

    const maxLoanAmount = calcMaxLoan(
        monthlyIncome,
        monthlyObligations,
        debtToIncomeRatio,
        periodicRate,
        totalPeriods
    );

    const maxMonthlyPayment = monthlyIncome * debtToIncomeRatio;
    const availableMonthlyPayment = maxMonthlyPayment - monthlyObligations;

    const result = {
        monthlyIncome,
        monthlyObligations,
        annualRate,
        termYears,
        debtToIncomeRatio,
        maxLoanAmount,
        maxMonthlyPayment,
        availableMonthlyPayment
    };

    renderResult(form, result);
    setFormResult('sposobnost', form, result);
}

function recalculateForm(form) {
    if (!form) return;
    const calcType = form.dataset.calcType;

    try {
        if (calcType === 'variable') {
            runVariableCalc(form);
        } else if (calcType === 'fixed') {
            runMortgageCalc(form, calcType);
        } else if (isYoungType(calcType)) {
            runMladeCalc(form);
        } else if (calcType === 'sposobnost') {
            runSposobnostCalc(form);
        }
    } catch (error) {
        const panel = getPanel(calcType);
        setPanelMessage(panel, 'Proverite vrednosti u poljima i pokušajte ponovo.', 'error');
    }
}

function parseAmortizationRows(apiPayload) {
    if (Array.isArray(apiPayload?.amortization)) return apiPayload.amortization;
    const schedule = apiPayload?.schedule;
    if (!schedule) return [];
    if (Array.isArray(schedule.rows)) return schedule.rows;
    if (Array.isArray(schedule)) return schedule;
    return [];
}

function invalidateAmortizationForType(type) {
    const normalizedType = normalizeCalcType(type);
    uiCtx.amortizationRowsByType.delete(normalizedType);

    const panel = getPanel(normalizedType);
    const wrap = panel ? panel.querySelector('.calc-amortization') : null;
    const body = wrap ? wrap.querySelector('tbody') : null;
    const showAllBtn = wrap ? wrap.querySelector('.calc-show-all') : null;

    batchDomWrite(() => {
        if (wrap) {
            wrap.hidden = true;
        }

        if (body) {
            body.textContent = '';
        }

        if (showAllBtn) {
            showAllBtn.hidden = true;
        }
    });
}

function renderAmortization(panel, rows) {
    const wrap = panel ? panel.querySelector('.calc-amortization') : null;
    const body = wrap ? wrap.querySelector('tbody') : null;
    const showAllBtn = wrap ? wrap.querySelector('.calc-show-all') : null;
    if (!wrap || !body) return;

    const renderRows = (limit = 5) => {
        const subset = rows.slice(0, limit);
        batchDomWrite(() => {
            body.textContent = '';

            subset.forEach((row) => {
                const month = row.period || row.monthIndex || '—';
                const isPhaseTransition = row.phaseTransition === true;

                const tr = document.createElement('tr');
                if (isPhaseTransition) {
                    tr.classList.add('calc-am-transition');
                    tr.dataset.phaseTransition = 'true';
                }

                const monthCell = document.createElement('td');
                monthCell.textContent = String(month);
                tr.appendChild(monthCell);

                const paidCell = document.createElement('td');
                paidCell.textContent = formatCurrency(row.paid || 0);
                tr.appendChild(paidCell);

                const principalCell = document.createElement('td');
                principalCell.textContent = formatCurrency(row.principal || 0);
                tr.appendChild(principalCell);

                const interestCell = document.createElement('td');
                interestCell.textContent = formatCurrency(row.interest || 0);
                tr.appendChild(interestCell);

                const balanceCell = document.createElement('td');
                balanceCell.textContent = formatCurrency(row.balance || 0);
                tr.appendChild(balanceCell);

                body.appendChild(tr);

                if (isPhaseTransition) {
                    const transitionRow = document.createElement('tr');
                    transitionRow.className = 'calc-am-transition-label';

                    const transitionCell = document.createElement('td');
                    transitionCell.colSpan = 5;
                    transitionCell.className = 'calc-am-transition-text';
                    transitionCell.textContent = row.phaseLabel || '↓ Varijabilna stopa počinje (EURIBOR + marža) ↓';

                    transitionRow.appendChild(transitionCell);
                    body.appendChild(transitionRow);
                }
            });
        });
    };

    renderRows(5);

    batchDomWrite(() => {
        wrap.hidden = false;
        if (showAllBtn) {
            showAllBtn.hidden = rows.length <= 5;
        }
    });

    if (showAllBtn) {
        showAllBtn.onclick = () => {
            renderRows(rows.length);
            batchDomWrite(() => {
                showAllBtn.hidden = true;
            });
        };
    }
}

async function fetchJson(url, payload) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Request failed: ${response.status}`);
    }

    return response.json();
}

function guardByGate(button, calcType, form, panel) {
    const gate = button.dataset.gate;
    if (!gate) return false;

    if (gate === 'login' && !uiCtx.state.isAuthenticated) {
        const action = button.dataset.action;
        let next;

        if (action === 'loan-schedule') {
            next = buildLoanScheduleHref(calcType, form);
        } else {
            const params = serializeFormToParams(form).toString();
            next = buildCalculatorUrl(calcType, params);
        }

        if (action === 'save') {
            next += (next.includes('?') ? '&' : '?') + 'pendingSave=1';
        }
        const loginHref = `/login?next=${encodeURIComponent(next)}`;
        let navigatedToLogin = false;
        try {
            window.location.href = loginHref;
            navigatedToLogin = window.location.pathname === '/login';
        } catch (error) {
            navigatedToLogin = false;
        }

        if (!navigatedToLogin && window.location.pathname !== '/login' && window.history && typeof window.history.replaceState === 'function') {
            window.history.replaceState({}, '', loginHref);
        }
        return true;
    }

    if (
        gate === 'premium'
        && button.dataset.action !== 'pdf'
        && String(uiCtx.state.licence || '').toUpperCase() !== 'PREMIUM'
    ) {
        setPanelMessage(panel, 'PDF izvoz je dostupan u Premijum paketu. Preusmeravanje na cenovnik...', 'warn');
        setTimeout(() => {
            let navigatedToPricing = false;
            try {
                window.location.href = '/pricing';
                navigatedToPricing = window.location.pathname === '/pricing';
            } catch (error) {
                navigatedToPricing = false;
            }

            if (!navigatedToPricing && window.location.pathname !== '/pricing' && window.history && typeof window.history.replaceState === 'function') {
                window.history.replaceState({}, '', '/pricing');
            }
        }, 900);
        return true;
    }

    return false;
}

async function handleAmortization(calcType, form, panel) {
    const input = collectFormInput(form);
    let data;

    if (calcType === 'variable') {
        const payload = {
            loanAmount: input.loanAmount,
            totalYears: input.termYears,
            introYears: input.introPeriodYears,
            ...(input.introPeriodYears > 0 && { introRate: input.introRate }),
            euriborRate: input.euriborRate ?? null,
            euriborTenor: input.euriborTenor || '3M',
            bankMargin: input.bankMargin,
            paymentsPerYear: 12,
            includeAmortization: true
        };

        data = await fetchJson('/calculators/variable', payload);
    } else if (isYoungType(calcType)) {
        const payload = buildSubventionYoungRequestPayloadFromForm(form, true);

        const validationMessage = getSubventionYoungPayloadValidationMessage(payload);
        if (validationMessage) {
            setPanelMessage(panel, validationMessage, 'warn');
            return;
        }

        data = await fetchJson('/calculators/subvention-young', payload);
    } else {
        const resolvedCalcType = calcType === 'mortgage' ? 'fixed' : calcType;
        const hasValidMortgageInput = Number.isFinite(Number(input.loanAmount))
            && Number(input.loanAmount) > 0
            && Number.isFinite(Number(input.annualRate))
            && Number(input.annualRate) >= 0
            && Number.isFinite(Number(input.termYears))
            && Number(input.termYears) > 0
            && (resolvedCalcType === 'fixed' || resolvedCalcType === 'variable');

        if (!hasValidMortgageInput) {
            setPanelMessage(panel, 'Unesite iznos kredita, kamatnu stopu i period otplate pre prikaza amortizacije.', 'warn');
            return;
        }

        const payload = {
            loanAmount: Number(input.loanAmount),
            annualRate: Number(input.annualRate),
            termYears: Number(input.termYears),
            calcType: resolvedCalcType,
            paymentsPerYear: 12,
            includeAmortization: true,
            currency: 'EUR'
        };

        data = await fetchJson('/calculators/mortgage', payload);
    }

    const rows = parseAmortizationRows(data);
    uiCtx.amortizationRowsByType.set(calcType, rows);
    renderAmortization(panel, rows);
    setPanelMessage(panel, '', 'info');
}

function exportCsv(calcType, panel) {
    const rows = uiCtx.amortizationRowsByType.get(calcType) || [];
    if (!rows.length) {
        setPanelMessage(panel, 'Prvo prikažite tabelu amortizacije.', 'warn');
        return;
    }

    const header = ['Mesec', 'Rata', 'Glavnica', 'Kamata', 'Preostalo'];
    const lines = rows.map((row) => [
        row.period || row.monthIndex || '',
        Number(row.paid || 0).toFixed(2),
        Number(row.principal || 0).toFixed(2),
        Number(row.interest || 0).toFixed(2),
        Number(row.balance || 0).toFixed(2)
    ]);

    const csv = [header, ...lines]
        .map((line) => line.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    const srSlug = CALC_TYPE_SLUG_SR_CSV[calcType] || calcType;
    a.download = `amortizacija-${srSlug}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
}

async function exportPdf(calcType, form, panel) {
    if (uiCtx.isPdfBusy) {
        return;
    }

    setPdfBusyState(true);

    const token = getAuthToken();
    const headers = {
        'Content-Type': 'application/json'
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const inputJson = collectFormInput(form);
    const resultJson = uiCtx.lastResultByType.get(calcType) || parseJsonSafe(form.dataset.lastResult || '{}', {});
    const schedule = uiCtx.amortizationRowsByType.get(calcType) || [];
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
        abortController.abort();
    }, PDF_EXPORT_TIMEOUT_MS);

    try {
        const response = await fetch('/calculators/export', {
            method: 'POST',
            headers,
            signal: abortController.signal,
            body: JSON.stringify({
                calcType,
                inputJson,
                resultJson,
                includeAmortization: schedule.length > 0,
                schedule
            })
        });

        if (!response.ok) {
            setPanelMessage(panel, 'PDF izvoz trenutno nije dostupan.', 'error');
            return;
        }

        const blob = await response.blob();
        const fallbackName = `kalkulator-${CALC_TYPE_SLUG_SR_CSV[calcType] || calcType}.pdf`;
        const contentDisposition = response.headers.get('content-disposition') || '';
        const match = /filename="([^"]+)"/i.exec(contentDisposition);
        const fileName = match ? match[1] : fallbackName;

        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);

        setPanelMessage(panel, '', 'info');
    } catch (error) {
        setPanelMessage(panel, 'PDF izvoz trenutno nije dostupan.', 'error');
    } finally {
        clearTimeout(timeoutId);
        setPdfBusyState(false);
    }
}

function bindActionsForPanel(panel, form, calcType) {
    if (!panel || !form) return;

    panel.querySelectorAll('.calc-btn').forEach((button) => {
        button.addEventListener('click', async (event) => {
            const action = button.dataset.action;
            if (!action) return;

            if (uiCtx.isPdfBusy) {
                event.preventDefault();
                return;
            }

            if (action === 'loan-schedule') {
                if (guardByGate(button, calcType, form, panel)) {
                    event.preventDefault();
                }
                return;
            }

            event.preventDefault();

            if (guardByGate(button, calcType, form, panel)) {
                return;
            }

            try {
                if (action === 'amortization') {
                    await handleAmortization(calcType, form, panel);
                } else if (action === 'csv') {
                    exportCsv(calcType, panel);
                } else if (action === 'pdf') {
                    await exportPdf(calcType, form, panel);
                } else if (action === 'save') {
                    document.dispatchEvent(new CustomEvent('loanCalc:save-request', {
                        detail: getCurrentCalcSnapshot(calcType)
                    }));
                }
            } catch (error) {
                setPanelMessage(panel, 'Akcija nije uspela. Pokušajte ponovo.', 'error');
            }
        });
    });
}

function activateTab(type, updateHistory = true) {
    const normalizedType = normalizeCalcType(type);
    const tabs = document.querySelectorAll('.calc-tab');

    tabs.forEach((tab) => {
        const tabType = normalizeCalcType(tab.dataset.tab);
        const selected = tabType === normalizedType;
        batchDomWrite(() => {
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.setAttribute('tabindex', selected ? '0' : '-1');
            tab.classList.toggle('is-active', selected);
        });

        const panel = getPanel(tabType);
        if (panel) {
            batchDomWrite(() => {
                if (selected) {
                    panel.hidden = false;
                    panel.style.animation = 'none';
                    void panel.offsetHeight;
                    panel.style.animation = '';
                } else {
                    panel.hidden = true;
                }
            });
        }
    });

    uiCtx.activeType = normalizedType;
    if (updateHistory) {
        updateUrlForType(normalizedType);
    }

    showBannerForTab(normalizedType);

    const form = getForm(normalizedType);
    recalculateForm(form);
}

function bindTabs() {
    document.querySelectorAll('.calc-tab').forEach((tab) => {
        tab.addEventListener('click', () => {
            const type = tab.dataset.tab;
            activateTab(type, true);
        });

        tab.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

            const tabs = Array.from(document.querySelectorAll('.calc-tab'));
            const index = tabs.indexOf(tab);
            const nextIndex = event.key === 'ArrowRight'
                ? (index + 1) % tabs.length
                : (index - 1 + tabs.length) % tabs.length;

            event.preventDefault();
            tabs[nextIndex].focus();
            activateTab(tabs[nextIndex].dataset.tab, true);
        });
    });
}

function prefillFromUrl(defaultType) {
    const params = new URLSearchParams(window.location.search);
    const form = getForm(normalizeCalcType(defaultType));
    if (!form) return;

    params.forEach((value, key) => {
        const input = getFormInput(form, key);
        if (input) {
            input.value = value;
        }
    });
}

function registerForms() {
    document.querySelectorAll('.calc-form').forEach((form) => {
        const type = normalizeCalcType(form.dataset.calcType);
        form.dataset.calcType = type;
        uiCtx.formsByType.set(type, form);

        if (type === CANONICAL_YOUNG_TYPE) {
            ensureMladeModelInputs(form);
        }

        const panel = form.closest('.calc-panel');
        if (panel) {
            uiCtx.panelsByType.set(type, panel);
            bindActionsForPanel(panel, form, type);
        }

        const debounced = debounce(() => {
            invalidateAmortizationForType(type);
            recalculateForm(form);
            if (uiCtx.activeType === type) {
                updateUrlForType(type);
            }
        }, 400);

        form.addEventListener('input', debounced);
        form.addEventListener('change', debounced);
    });
}

export function getCurrentCalcSnapshot(type = uiCtx.activeType) {
    const form = getForm(type);
    if (!form) {
        return {
            calcType: type,
            inputJson: {},
            resultJson: {}
        };
    }

    return {
        calcType: type,
        inputJson: collectFormInput(form),
        resultJson: uiCtx.lastResultByType.get(type) || parseJsonSafe(form.dataset.lastResult || '{}', {})
    };
}

export function loadScenarioIntoForm(calcType, inputJson) {
    const normalizedType = normalizeCalcType(calcType);
    const form = getForm(normalizedType);
    if (!form || !inputJson || typeof inputJson !== 'object') return;

    if (normalizedType === CANONICAL_YOUNG_TYPE) {
        ensureMladeModelInputs(form);
    }

    Object.entries(inputJson).forEach(([key, value]) => {
        const input = getFormInput(form, key);
        if (!input) {
            if (normalizedType === CANONICAL_YOUNG_TYPE && MLADE_MODEL_FIELDS.includes(key)) {
                const modelInput = getFormInput(form, key);
                if (modelInput) {
                    modelInput.value = value === null || value === undefined ? '' : String(value);
                }
            }
            return;
        }

        if (value === null || value === undefined) {
            input.value = '';
            return;
        }

        input.value = String(value);
    });

    invalidateAmortizationForType(normalizedType);
    activateTab(normalizedType, true);
    recalculateForm(form);
}

export function initCalcUI(config = {}, state = {}) {
    uiCtx.config = config;
    uiCtx.state = state;

    registerForms();
    bindTabs();
    initBannerDismiss();

    const pathType = window.location.pathname.startsWith('/calculators/')
        ? window.location.pathname.replace('/calculators/', '').split('/')[0]
        : '';
    const initialType = normalizeCalcType(state.defaultType || pathType || 'variable');
    prefillFromUrl(initialType);
    activateTab(initialType, false);
    showBannerForTab(initialType);

    const variableForm = getForm('variable');
    const euriborField = getFormInput(variableForm, 'euriborRate');
    const euriborFromConfig = toNumber(config?.euribor3mRate, 0);
    if (euriborField && euriborFromConfig > 0) {
        euriborField.value = String(euriborFromConfig);
    }

    const euriborHint = variableForm ? variableForm.querySelector('[data-euribor-hint]') : null;
    if (euriborHint) {
        const updatedAt = config?.euriborUpdatedAt;
        if (updatedAt) {
            try {
                const d = new Date(updatedAt);
                const dateStr = d.toISOString().slice(0, 10);
                euriborHint.textContent = `Live • Ažurirano: ${dateStr}`;
            } catch {
                euriborHint.textContent = 'Live';
            }
        }
    }

    const euriborTenorSelect = variableForm ? variableForm.querySelector('#euribor-tenor-select') : null;
    if (euriborTenorSelect) {
        const euriborRates = {
            '3M': toNumber(config?.euribor3mRate, 0),
            '6M': toNumber(config?.euribor6mRate, 0),
            '12M': toNumber(config?.euribor12mRate, 0)
        };
        const applyTenorRate = (tenor) => {
            const rate = euriborRates[tenor];
            if (euriborField && rate > 0) {
                euriborField.value = String(rate);
                runVariableCalc(variableForm);
            }
        };
        euriborTenorSelect.addEventListener('change', () => {
            applyTenorRate(euriborTenorSelect.value);
        });
        applyTenorRate('3M');
    }

    const youngForm = getForm(CANONICAL_YOUNG_TYPE);
    const youngEuriborField = getFormInput(youngForm, 'euriborRate');
    const youngHint = youngForm ? youngForm.querySelector('[data-young-euribor-hint]') : null;
    const youngTenorSelect = youngForm ? youngForm.querySelector('#subvention-young-euribor-tenor') : null;
    if (youngForm && youngTenorSelect && youngEuriborField) {
        const youngRates = {
            '3M': toNumber(config?.euribor3mRate, 0),
            '6M': toNumber(config?.euribor6mRate, 0)
        };
        const applyYoungTenor = (tenor) => {
            const rate = youngRates[tenor];
            if (rate > 0) {
                youngEuriborField.value = String(rate);
                runMladeCalc(youngForm);
            }
        };
        youngTenorSelect.addEventListener('change', () => {
            applyYoungTenor(youngTenorSelect.value);
        });
        applyYoungTenor(youngTenorSelect.value || '6M');
    }
    if (youngHint) {
        const updatedAt = config?.euriborUpdatedAt;
        if (updatedAt) {
            try {
                const d = new Date(updatedAt);
                const dateStr = d.toISOString().slice(0, 10);
                youngHint.textContent = `Live • Ažurirano: ${dateStr}`;
            } catch {
                youngHint.textContent = 'Live';
            }
        }
    }

    const initialForm = getForm(initialType);
    document.querySelectorAll('.calc-tooltip-wrap').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            const wasActive = trigger.classList.contains('is-active');
            document.querySelectorAll('.calc-tooltip-wrap.is-active').forEach((t) => t.classList.remove('is-active'));
            if (!wasActive) trigger.classList.add('is-active');
        });
    });
    document.addEventListener('click', () => {
        document.querySelectorAll('.calc-tooltip-wrap.is-active').forEach((t) => t.classList.remove('is-active'));
    });

    recalculateForm(initialForm);
}
